import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SeeMoreInfoModal from "@tKgpco/Components/SeeMoreInfoModal";
import { productCarouselProductStyles as productStyle } from "@tKgpco/Overrides/Components/ProductCarouselProduct";
import ProductPartNumbers from "@tKgpco/Overrides/Components/ProductPartNumbers";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    isAuthenticated: state.context.session?.isAuthenticated && !state.context.session?.isGuest
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    innerContainer?: InjectableCss;
    labels?: InjectableCss;
    seeLeadTimeLink?: LinkPresentationProps;
    seeLeadTimeLinkGridItem?: GridItemProps;
    tooltipText?: InjectableCss;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
            padding: 0.625rem 0.75rem 0 0.75rem;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0.625rem 0.75rem 0 0;
                    `,
                    css`
                        padding: 0.625rem 0.75rem 0 0;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    innerContainer: {
        css: css`
            font-size: 14px !important;
            padding-bottom: 0rem;
            width: 100%;
            color: ${COLORS.lightBlack};
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 20,
            },
        },
    },
    brandGridItem: {
        width: 12,
        css: css`
            a {
                span {
                    font-size: 14px;
                }
            }
        `,
    },
    descriptionGridItem: {
        width: 12,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            ${GridItemStyle} {
                padding: 3px;
            }
        `,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
        css: css`
            ${GridItemStyle} {
                align-items: center;
                justify-content: flex-end;
                font-weight: 600;
                width: inherit;
                padding: 0;
            }
            ${GridOffset} {
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                width: calc(100% + 3px) !important;
                            `,
                            css`
                                width: calc(100% + 10px) !important;
                            `,
                            css`
                                width: calc(100% + 0px) !important;
                            `,
                        ],
                        "min",
                    )}
            }
        `,
    },
    attributesGridItem: {
        width: 12,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    labels: {
        css: css`
            font-size: 14px;
            margin-bottom: 5px;
        `
    },
    seeLeadTimeLink: {
        css: css`
            & :hover .tooltipText {
                visibility: visible;
            }
        `
    },
    seeLeadTimeLinkGridItem: {
        width: 5,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-end;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            > span {
                background: #e1e7ed;
                padding: 3px 4px;
                border-radius: 4px;
                > span {
                    color: #353432 !important;
                    font-size: ${TYPOGRAPHY.size.vTiny};
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 10px !important;
                    }
                }
            }
        `,
    },
    tooltipText: {
        css: css`
            visibility: hidden;
            width: auto;
            background-color: black;
            color: #fff;
            text-align: center;
            border-radius: 6px;
            padding: 5px 6px;
            opacity: 0.8;
            position: absolute;
            z-index: 1;
            margin-top: 10px;
        `
    }
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    isAuthenticated
}: Props) => {
    const { product, productInfo } = productContext;
    if (!product) {
        return null;
    }
    let leadtime = "";
    if(productInfo?.pricing?.additionalResults && Object.keys(productInfo?.pricing?.additionalResults).length > 0) {
        leadtime = productInfo?.pricing?.additionalResults?.leadTime;
    }
    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    return (
        <GridContainer {...styles.container}>
            {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription
                        product={productContext}
                        extendedStyles={productStyle.productDescriptionStyles}
                    />
                </GridItem>
            )}
            <StyledWrapper {...styles.innerContainer}>
                {showPartNumbers && (
                    <GridItem {...styles.partNumbersGridItem}>
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            extendedStyles={productStyle.productPartNumbersStyles}
                            specification={product.specifications}
                            product={productContext}
                        />
                    </GridItem>
                )}
                {variantDisplayTrait && (
                    <GridItem {...styles.swatchGridItem}>
                        <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                    </GridItem>
                )}
                {showTextBadges && textBadges && (
                    <GridItem {...styles.textBadgeGridItem}>
                        <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                    </GridItem>
                )}
                {showImageBadges && imageBadges && (
                    <GridItem {...styles.imageBadgeGridItem}>
                        <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                    </GridItem>
                )}
                {showAvailability && (
                    <GridItem {...styles.availabilityGridItem}>
                        <ProductContextAvailability extendedStyles={productStyle.productAvailabilityStyle} />
                    </GridItem>
                )}
                {showAttributes && (
                    <GridItem {...styles.attributesGridItem}>
                        <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                    </GridItem>
                )}
                {isAuthenticated && leadtime && leadtime !== "" && productInfo?.pricing?.additionalResults?.showContactUsMessage !== "true" && <GridItem {...styles.seeLeadTimeLinkGridItem}>
                    <Link
                        {...styles.seeLeadTimeLink}
                        data-test-selector="seeLeadTimeLink"
                    >
                        {translate("See Lead Time")}
                        <StyledWrapper className="tooltipText" {...styles.tooltipText}>{`${leadtime} days`}</StyledWrapper>
                    </Link>
                </GridItem>}
                {isAuthenticated && <SeeMoreInfoModal product={product}/>}
            </StyledWrapper>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
